import { ReactNode, SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/utils';

/**
 * A native `<select>` that looks like the rest of the design system.
 *
 * `SelectField` (Radix) is the default choice and should stay it — but it takes
 * a flat `string[]` and cannot render `<optgroup>`, and the station wizard's
 * product picker groups by use case on purpose (#2465, QA defect I). A native
 * select is the right element there; what it was missing is the styling.
 *
 * The browser draws its own arrow hard against the right border and ignores the
 * element's padding, so the wizard's picker had a visibly wider gap on the icon
 * side than on the text side (QA #2439, defect F). `appearance-none` removes it
 * and the chevron is rendered at the same inset as the text, from the same
 * `lucide-react` icon and the same `text-foreground/60` token `SelectField`'s
 * trigger uses — so the two controls read as one family.
 *
 * `pr-9` reserves room for the icon; without it a long option name runs under
 * the chevron. The wrapper is `relative` and the icon `pointer-events-none`, so
 * clicking the arrow still opens the native dropdown.
 *
 * Twelve other native selects in admin-ui still carry the browser arrow. They
 * are not migrated here — this fixes the one QA reported and gives the next one
 * somewhere to go rather than another inline copy.
 */
interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    /** `<option>` and `<optgroup>` elements. */
    children: ReactNode;
}

const NativeSelect = ({ className, children, ...selectProps }: NativeSelectProps) => (
    <div className={'relative'}>
        <select
            {...selectProps}
            className={cn(
                'w-full appearance-none rounded-lg border border-foreground/20 bg-transparent',
                'px-3 py-2 pr-9 text-sm text-foreground',
                'focus:outline-none focus:border-primary',
                selectProps.disabled && 'cursor-not-allowed opacity-60',
                className,
            )}
        >
            {children}
        </select>
        <ChevronDown
            className={'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60'}
            aria-hidden={'true'}
        />
    </div>
);

export default NativeSelect;
